import { useEffect, useState } from "react";
import { X, VideoCamera, NotePencil } from "@phosphor-icons/react";
import { ExpandSvg, CollapseSvg } from "../icons";

const CONFIRM_MS = 3000;

export function AgentHeader({
  avatarUrl,
  onClose,
  closeLabel = "Close chat",
  panelExpanded,
  onToggleExpand,
  onAvatarClick,
  videoActive,
  onNewChat,
  newChatConfirm = false,
}: {
  avatarUrl?: string;
  onClose: () => void;
  closeLabel?: string;
  panelExpanded: boolean;
  onToggleExpand: () => void;
  onAvatarClick?: () => void;
  videoActive?: boolean;
  // Omit to hide the "New chat" button (nothing to reset yet).
  onNewChat?: () => void;
  // Mid voice call: the first tap only arms the button ("Tap again to end the
  // call and start over"); a second tap within CONFIRM_MS resets.
  newChatConfirm?: boolean;
}) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), CONFIRM_MS);
    return () => clearTimeout(t);
  }, [armed]);
  useEffect(() => {
    if (!newChatConfirm) setArmed(false);
  }, [newChatConfirm]);
  const handleNewChat = () => {
    if (newChatConfirm && !armed) {
      setArmed(true);
      return;
    }
    setArmed(false);
    onNewChat?.();
  };
  const newChatLabel = armed ? "Tap again to end the call and start over" : "New chat";
  return (
    <div className="lk-widget-header">
      <div className="lk-widget-header-left">
        {avatarUrl && (
          <button
            className={`lk-widget-avatar-btn${videoActive ? " active" : ""}`}
            onClick={onAvatarClick}
            disabled={!onAvatarClick}
            aria-label={videoActive ? "Hide video" : "Show video"}
          >
            <img className="lk-widget-avatar" src={avatarUrl} alt="Courtney" />
            {onAvatarClick && (
              <span className="lk-widget-avatar-badge">
                <VideoCamera size={12} weight="fill" />
              </span>
            )}
          </button>
        )}
        <div className="lk-widget-header-text">
          <span className="lk-widget-header-name">Courtney™</span>
          <span className="lk-widget-header-sub">AI Concierge</span>
        </div>
      </div>
      <div className="lk-widget-header-right">
        {onNewChat && (
          <button
            className={`lk-widget-new-chat${armed ? " lk-widget-new-chat--armed" : ""}`}
            onClick={handleNewChat}
            aria-label={newChatLabel}
            title={newChatLabel}
          >
            <NotePencil size={17} weight="bold" />
            {armed && <span className="lk-widget-new-chat-hint">End call?</span>}
          </button>
        )}
        <button
          className="lk-widget-expand"
          onClick={onToggleExpand}
          aria-label={panelExpanded ? "Collapse panel" : "Expand panel"}
        >
          {panelExpanded ? CollapseSvg : ExpandSvg}
        </button>
        <button className="lk-widget-close" onClick={onClose} aria-label={closeLabel}>
          <X size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}
